"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.presentPacketData = exports.splitPendingPackets = exports.timeGreater = exports.heightGreater = exports.parseAck = exports.parseAcksFromTxEvents = exports.parsePacket = exports.parseHeightAttribute = exports.parsePacketsFromTendermintEvents = exports.parsePacketsFromEvents = exports.parsePacketsFromBlockResult = exports.buildClientState = exports.buildConsensusState = exports.secondsFromDateNanos = exports.timestampFromDateNanos = exports.mapRpcPubKeyToProto = exports.may = exports.parseRevisionNumber = exports.subtractBlock = exports.ensureIntHeight = exports.toIntHeight = exports.createDeliverTxFailureMessage = void 0;
const encoding_1 = require("@cosmjs/encoding");
const stargate_1 = require("@cosmjs/stargate");
const proofs_1 = require("cosmjs-types/confio/proofs");
const timestamp_1 = require("cosmjs-types/google/protobuf/timestamp");
const channel_1 = require("cosmjs-types/ibc/core/channel/v1/channel");
const tendermint_1 = require("cosmjs-types/ibc/lightclients/tendermint/v1/tendermint");
const long_1 = __importDefault(require("long"));
function createDeliverTxFailureMessage(result) {
    return `Error when broadcasting tx ${result.transactionHash} at height ${result.height}. Code: ${result.code}; Raw log: ${result.rawLog}`;
}
exports.createDeliverTxFailureMessage = createDeliverTxFailureMessage;
function toIntHeight(height) {
    var _a, _b;
    return (_b = (_a = height === null || height === void 0 ? void 0 : height.revisionHeight) === null || _a === void 0 ? void 0 : _a.toNumber()) !== null && _b !== void 0 ? _b : 0;
}
exports.toIntHeight = toIntHeight;
function ensureIntHeight(height) {
    if (typeof height === 'number') {
        return height;
    }
    return toIntHeight(height);
}
exports.ensureIntHeight = ensureIntHeight;
function subtractBlock(height, count = 1) {
    return {
        revisionNumber: height.revisionNumber,
        revisionHeight: height.revisionHeight.subtract(count),
    };
}
exports.subtractBlock = subtractBlock;
const regexRevNum = new RegExp('-([1-9][0-9]*)$');
function parseRevisionNumber(chainId) {
    const match = chainId.match(regexRevNum);
    if (match && match.length >= 2) {
        return long_1.default.fromString(match[1]);
    }
    return long_1.default.ZERO;
}
exports.parseRevisionNumber = parseRevisionNumber;
// may will run the transform if value is defined, otherwise returns undefined
function may(transform, value) {
    return value === undefined || value === null ? undefined : transform(value);
}
exports.may = may;
function mapRpcPubKeyToProto(pubkey) {
    if (pubkey === undefined) {
        return undefined;
    }
    if (pubkey.algorithm == 'ed25519') {
        return {
            ed25519: pubkey.data,
            secp256k1: undefined,
        };
    }
    else if (pubkey.algorithm == 'secp256k1') {
        return {
            ed25519: undefined,
            secp256k1: pubkey.data,
        };
    }
    else {
        throw new Error(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `Unknown validator pubkey type: ${pubkey.algorithm}`);
    }
}
exports.mapRpcPubKeyToProto = mapRpcPubKeyToProto;
function timestampFromDateNanos(date) {
    var _a;
    const nanos = (date.getTime() % 1000) * 1000000 + ((_a = date.nanoseconds) !== null && _a !== void 0 ? _a : 0);
    return timestamp_1.Timestamp.fromPartial({
        seconds: long_1.default.fromNumber(date.getTime() / 1000),
        nanos,
    });
}
exports.timestampFromDateNanos = timestampFromDateNanos;
function secondsFromDateNanos(date) {
    return Math.floor(date.getTime() / 1000);
}
exports.secondsFromDateNanos = secondsFromDateNanos;
function buildConsensusState(header) {
    return tendermint_1.ConsensusState.fromPartial({
        timestamp: timestampFromDateNanos(header.time),
        root: {
            hash: header.appHash,
        },
        nextValidatorsHash: header.nextValidatorsHash,
    });
}
exports.buildConsensusState = buildConsensusState;
// Note: we hardcode a number of assumptions, like trust level, clock drift, and assume revisionNumber is 1
function buildClientState(chainId, unbondingPeriodSec, trustPeriodSec, height) {
    // Copied here until https://github.com/confio/ics23/issues/36 is resolved
    // https://github.com/confio/ics23/blob/master/js/src/proofs.ts#L11-L26
    const iavlSpec = {
        leafSpec: {
            prefix: Uint8Array.from([0]),
            hash: proofs_1.HashOp.SHA256,
            prehashValue: proofs_1.HashOp.SHA256,
            prehashKey: proofs_1.HashOp.NO_HASH,
            length: proofs_1.LengthOp.VAR_PROTO,
        },
        innerSpec: {
            childOrder: [0, 1],
            minPrefixLength: 4,
            maxPrefixLength: 12,
            childSize: 33,
            hash: proofs_1.HashOp.SHA256,
        },
    };
    const tendermintSpec = {
        leafSpec: {
            prefix: Uint8Array.from([0]),
            hash: proofs_1.HashOp.SHA256,
            prehashValue: proofs_1.HashOp.SHA256,
            prehashKey: proofs_1.HashOp.NO_HASH,
            length: proofs_1.LengthOp.VAR_PROTO,
        },
        innerSpec: {
            childOrder: [0, 1],
            minPrefixLength: 1,
            maxPrefixLength: 1,
            childSize: 32,
            hash: proofs_1.HashOp.SHA256,
        },
    };
    return tendermint_1.ClientState.fromPartial({
        chainId,
        trustLevel: {
            numerator: long_1.default.fromInt(1),
            denominator: long_1.default.fromInt(3),
        },
        unbondingPeriod: {
            seconds: long_1.default.fromNumber(unbondingPeriodSec),
        },
        trustingPeriod: {
            seconds: long_1.default.fromNumber(trustPeriodSec),
        },
        maxClockDrift: {
            seconds: long_1.default.fromNumber(20),
        },
        latestHeight: height,
        proofSpecs: [iavlSpec, tendermintSpec],
        upgradePath: ['upgrade', 'upgradedIBCState'],
        allowUpdateAfterExpiry: false,
        allowUpdateAfterMisbehaviour: false,
    });
}
exports.buildClientState = buildClientState;
function parsePacketsFromBlockResult(result) {
    return parsePacketsFromTendermintEvents([
        ...result.beginBlockEvents,
        ...result.endBlockEvents,
    ]);
}
exports.parsePacketsFromBlockResult = parsePacketsFromBlockResult;
/** Those events are normalized to strings already in CosmJS */
function parsePacketsFromEvents(events) {
    return events.filter(({ type }) => type === 'send_packet').map(parsePacket);
}
exports.parsePacketsFromEvents = parsePacketsFromEvents;
/**
 * Takes a list of events, finds the send_packet events, stringifies attributes
 * and parsed the events into `Packet`s.
 */
function parsePacketsFromTendermintEvents(events) {
    return parsePacketsFromEvents(events.map(stargate_1.fromTendermintEvent));
}
exports.parsePacketsFromTendermintEvents = parsePacketsFromTendermintEvents;
function parseHeightAttribute(attribute) {
    var _a;
    const [timeoutRevisionNumber, timeoutRevisionHeight] = (_a = attribute === null || attribute === void 0 ? void 0 : attribute.split('-')) !== null && _a !== void 0 ? _a : [];
    if (!timeoutRevisionHeight || !timeoutRevisionNumber) {
        return undefined;
    }
    const revisionNumber = long_1.default.fromString(timeoutRevisionNumber);
    const revisionHeight = long_1.default.fromString(timeoutRevisionHeight);
    // note: 0 revisionNumber is allowed. If there is bad data, '' or '0-0', we will get 0 for the height
    if (revisionHeight.isZero()) {
        return undefined;
    }
    return { revisionHeight, revisionNumber };
}
exports.parseHeightAttribute = parseHeightAttribute;
function parsePacket({ type, attributes }) {
    if (type !== 'send_packet') {
        throw new Error(`Cannot parse event of type ${type}`);
    }
    const attributesObj = attributes.reduce((acc, { key, value }) => ({
        ...acc,
        [key]: value,
    }), {});
    return channel_1.Packet.fromPartial({
        sequence: may(long_1.default.fromString, attributesObj.packet_sequence),
        /** identifies the port on the sending chain. */
        sourcePort: attributesObj.packet_src_port,
        /** identifies the channel end on the sending chain. */
        sourceChannel: attributesObj.packet_src_channel,
        /** identifies the port on the receiving chain. */
        destinationPort: attributesObj.packet_dst_port,
        /** identifies the channel end on the receiving chain. */
        destinationChannel: attributesObj.packet_dst_channel,
        /** actual opaque bytes transferred directly to the application module */
        data: attributesObj.packet_data
            ? (0, encoding_1.toUtf8)(attributesObj.packet_data)
            : undefined,
        /** block height after which the packet times out */
        timeoutHeight: parseHeightAttribute(attributesObj.packet_timeout_height),
        /** block timestamp (in nanoseconds) after which the packet times out */
        timeoutTimestamp: may(long_1.default.fromString, attributesObj.packet_timeout_timestamp),
    });
}
exports.parsePacket = parsePacket;
function parseAcksFromTxEvents(events) {
    return events
        .filter(({ type }) => type === 'write_acknowledgement')
        .map(parseAck);
}
exports.parseAcksFromTxEvents = parseAcksFromTxEvents;
function parseAck({ type, attributes }) {
    var _a, _b;
    if (type !== 'write_acknowledgement') {
        throw new Error(`Cannot parse event of type ${type}`);
    }
    const attributesObj = attributes.reduce((acc, { key, value }) => ({
        ...acc,
        [key]: value,
    }), {});
    const originalPacket = channel_1.Packet.fromPartial({
        sequence: may(long_1.default.fromString, attributesObj.packet_sequence),
        /** identifies the port on the sending chain. */
        sourcePort: attributesObj.packet_src_port,
        /** identifies the channel end on the sending chain. */
        sourceChannel: attributesObj.packet_src_channel,
        /** identifies the port on the receiving chain. */
        destinationPort: attributesObj.packet_dst_port,
        /** identifies the channel end on the receiving chain. */
        destinationChannel: attributesObj.packet_dst_channel,
        /** actual opaque bytes transferred directly to the application module */
        data: (0, encoding_1.toUtf8)((_a = attributesObj.packet_data) !== null && _a !== void 0 ? _a : ''),
        /** block height after which the packet times out */
        timeoutHeight: parseHeightAttribute(attributesObj.packet_timeout_height),
        /** block timestamp (in nanoseconds) after which the packet times out */
        timeoutTimestamp: may(long_1.default.fromString, attributesObj.packet_timeout_timestamp),
    });
    const acknowledgement = (0, encoding_1.toUtf8)((_b = attributesObj.packet_ack) !== null && _b !== void 0 ? _b : '');
    return {
        acknowledgement,
        originalPacket,
    };
}
exports.parseAck = parseAck;
// return true if a > b, or a undefined
function heightGreater(a, b) {
    if (a === undefined) {
        return true;
    }
    // comparing longs made some weird issues (maybe signed/unsigned)?
    // convert to numbers to compare safely
    const [numA, heightA, numB, heightB] = [
        a.revisionNumber.toNumber(),
        a.revisionHeight.toNumber(),
        b.revisionNumber.toNumber(),
        b.revisionHeight.toNumber(),
    ];
    const valid = numA > numB || (numA == numB && heightA > heightB);
    return valid;
}
exports.heightGreater = heightGreater;
// return true if a > b, or a 0
// note a is nanoseconds, while b is seconds
function timeGreater(a, b) {
    if (a === undefined || a.isZero()) {
        return true;
    }
    const valid = a.toNumber() > b * 1000000000;
    return valid;
}
exports.timeGreater = timeGreater;
// take height and time from receiving chain to see which packets have timed out
// return [toSubmit, toTimeout].
// you can advance height, time a block or two into the future if you wish a margin of error
function splitPendingPackets(currentHeight, currentTime, // in seconds
packets) {
    return packets.reduce((acc, packet) => {
        const validPacket = heightGreater(packet.packet.timeoutHeight, currentHeight) &&
            timeGreater(packet.packet.timeoutTimestamp, currentTime);
        return validPacket
            ? {
                ...acc,
                toSubmit: [...acc.toSubmit, packet],
            }
            : {
                ...acc,
                toTimeout: [...acc.toTimeout, packet],
            };
    }, {
        toSubmit: [],
        toTimeout: [],
    });
}
exports.splitPendingPackets = splitPendingPackets;
function presentPacketData(data) {
    try {
        return JSON.parse((0, encoding_1.fromUtf8)(data));
    }
    catch (_a) {
        return { hex: (0, encoding_1.toHex)(data) };
    }
}
exports.presentPacketData = presentPacketData;
//# sourceMappingURL=data:application/json;base64,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